import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowRight } from "lucide-react";
import { api, formatINR } from "@/api";
import { useCart } from "@/context/CartContext";
import { trackBeginCheckout } from "@/lib/analytics";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const FIELDS = [
  { name: "full_name", label: "Full Name", type: "text", full: true },
  { name: "email", label: "Email", type: "email" },
  { name: "phone", label: "Phone", type: "tel" },
  { name: "address", label: "Address", type: "text", full: true },
  { name: "city", label: "City", type: "text" },
  { name: "state", label: "State", type: "text" },
  { name: "pincode", label: "Pincode", type: "text" },
];

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", address: "", city: "", state: "", pincode: "" });
  const [submitting, setSubmitting] = useState(false);
  const [config, setConfig] = useState({ razorpay_enabled: false, razorpay_key_id: "" });
  const orderPlaced = useRef(false);

  useEffect(() => {
    api.get("/config").then(({ data }) => setConfig(data));
  }, []);

  useEffect(() => {
    if (items.length === 0 && !orderPlaced.current) navigate("/cart");
  }, [items, navigate]);

  const total = subtotal;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    for (const f of FIELDS) {
      if (!form[f.name].trim()) {
        toast.error(`Please enter your ${f.label.toLowerCase()}`);
        return false;
      }
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Please enter a valid email");
      return false;
    }
    return true;
  };

  const placeOrder = async () => {
    if (!validate()) return;
    setSubmitting(true);
    trackBeginCheckout(items, total);
    try {
      const { data: order } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, image: i.image, variant: i.variant, customization: i.customization })),
        customer: form,
      });

      if (config.razorpay_enabled && order.razorpay_order_id) {
        const ok = await loadRazorpay();
        if (!ok) throw new Error("Razorpay failed to load");
        const rzp = new window.Razorpay({
          key: config.razorpay_key_id,
          amount: order.total * 100,
          currency: "INR",
          name: "J&B Golf · PING India",
          description: `Order ${order.id.slice(0, 8)}`,
          order_id: order.razorpay_order_id,
          prefill: { name: form.full_name, email: form.email, contact: form.phone },
          theme: { color: "#E31837" },
          handler: async (res) => {
            try {
              await api.post("/orders/verify", {
                order_id: order.id,
                razorpay_order_id: res.razorpay_order_id,
                razorpay_payment_id: res.razorpay_payment_id,
                razorpay_signature: res.razorpay_signature,
              });
              orderPlaced.current = true;
              clearCart();
              navigate(`/order/${order.id}`);
            } catch (err) {
              toast.error("Payment verification failed. If money was debited, please contact us with your order ID.");
              setSubmitting(false);
            }
          },
          modal: { ondismiss: () => setSubmitting(false) },
        });
        rzp.on("payment.failed", (resp) => {
          toast.error(resp?.error?.description || "Payment failed. Please try again.");
          setSubmitting(false);
        });
        rzp.open();
      } else {
        // Payments not yet configured — place order as pending
        orderPlaced.current = true;
        toast.success("Order placed");
        navigate(`/order/${order.id}`);
        clearCart();
      }
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="container-ping py-14" data-testid="checkout-page">
      <h1 className="heading-1 mb-10">Checkout</h1>
      <div className="grid lg:grid-cols-[1fr_380px] gap-12">
        {/* Form */}
        <div>
          <h2 className="heading-3 mb-6">Shipping Details</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {FIELDS.map((f) => (
              <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
                <Label htmlFor={f.name} className="label-caps text-muted-foreground">{f.label}</Label>
                <Input
                  id={f.name}
                  type={f.type}
                  value={form[f.name]}
                  onChange={(e) => setField(f.name, e.target.value)}
                  className="mt-2 rounded-none border-border focus-visible:ring-2 focus-visible:ring-[#0A0A0A]"
                  data-testid={`checkout-${f.name}`}
                />
              </div>
            ))}
            <div className="sm:col-span-2">
              <Label htmlFor="notes" className="label-caps text-muted-foreground">Order Notes (optional)</Label>
              <Textarea id="notes" className="mt-2 rounded-none" data-testid="checkout-notes"
                onChange={(e) => setField("notes", e.target.value)} />
            </div>
          </div>
          {!config.razorpay_enabled && (
            <p className="mt-6 text-sm bg-[#F4F4F5] border border-border p-4 text-muted-foreground" data-testid="payment-notice">
              Online payment (Razorpay) will be enabled shortly. Your order will be placed and our team will contact you to confirm payment.
            </p>
          )}
        </div>

        {/* Summary */}
        <div className="lg:sticky lg:top-28 self-start border border-[#0A0A0A] p-6 h-fit" data-testid="checkout-summary">
          <h2 className="heading-3 mb-6">Order Summary</h2>
          <div className="space-y-4 max-h-64 overflow-auto mb-4">
            {items.map((i) => (
              <div key={i.product_id + (i.variant || "") + (i.customization || "")} className="flex gap-3 items-center">
                <img src={i.image} alt={i.name} className="w-12 h-12 object-cover bg-secondary border border-border" />
                <div className="flex-1 text-sm">
                  <p className="font-semibold leading-tight">{i.name}</p>
                  {i.variant && <p className="text-[11px] text-muted-foreground">{i.variant}</p>}
                  {i.customization && <p className="text-[11px] text-muted-foreground">Personalization: “{i.customization}”</p>}
                  <p className="text-muted-foreground">Qty {i.quantity}</p>
                </div>
                <span className="text-sm font-semibold">{formatINR(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="space-y-2 text-sm border-t border-border pt-4">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-semibold">{formatINR(subtotal)}</span></div>
            <div className="flex justify-between border-t border-border pt-2 mt-2 text-lg">
              <span className="font-head font-bold uppercase">Total</span>
              <span className="font-bold" data-testid="checkout-total">{formatINR(total)}</span>
            </div>
          </div>
          <button
            onClick={placeOrder}
            disabled={submitting}
            className="w-full mt-6 bg-[#E31837] text-white py-4 label-caps flex items-center justify-center gap-2 btn-transition hover:bg-[#b3122c] active:scale-[0.98] disabled:opacity-60"
            data-testid="place-order-btn"
          >
            {submitting ? "Processing..." : config.razorpay_enabled ? <>Pay {formatINR(total)} <Lock className="h-4 w-4" /></> : <>Place Order <ArrowRight className="h-4 w-4" /></>}
          </button>
          <Link to="/cart" className="block text-center mt-4 label-caps text-muted-foreground hover:text-[#0A0A0A] btn-transition">
            Back to Bag
          </Link>
        </div>
      </div>
    </div>
  );
}
